/**
 * `player.html` bootstrap — the entry point of the standalone player, and the
 * one place that knows how to *mount* one (the React wrapper reuses
 * `mountPlayer` so there is only ever one wiring).
 *
 * For an exported zip this file is the whole app: no React, no router, no SPA
 * CSS. Its job is four steps and nothing else:
 *
 *   1. find the bundle — `?src=`, else the `bundle.json` sitting next to this
 *      page (§4.6.4, "one layout everywhere"),
 *   2. mount `createPlayer(host, bundle, { bundleUrl, hud })` with the HUD this
 *      track owns,
 *   3. register the cache-first service worker and hand it the asset list,
 *   4. show a friendly page instead of a blank screen when any of that fails.
 *
 * A story still being made (a `stream` block in its bundle) goes on growing
 * after step 2: `watchStream` polls `bundle.json` and folds each new part into
 * the story that is playing (docs/STORY-STREAMING.md).
 */

import { AudioBus, createAudioPort } from './audio'
import { normalizeBundle } from './bundle'
import { clampRate } from './clock'
import { createHud, splitPrefs } from './hud'
import { createPlayer } from './index'
import { HttpError, fetchBundleJson, takePrefetched } from './prefetch'
import { isOpen, streamOf, watchBundle } from './stream'
import { SubtitleBand, applyPlayerUiState, placementFor } from './subtitles'
import { THEME } from './theme'

import type { Bundle } from '@/schema/story'

import type { HudHandle, HudPrefs } from './hud'
import type { CreatePlayerOptions, PlayerHandle } from './index'
import type { BundleWatcher, WatchOptions } from './stream'

// ---------------------------------------------------------------------------
// Bundle location
// ---------------------------------------------------------------------------

/**
 * `?src=` when given (resolved against the page, so `?src=../other/bundle.json`
 * works in an export), otherwise the sibling `bundle.json`.
 */
export function resolveBundleUrl(href: string, override?: string): URL {
  const page = new URL(href)
  const raw = override ?? page.searchParams.get('src') ?? 'bundle.json'
  return new URL(raw, page)
}

/**
 * The player only plays bundles from its own origin: `?src=https://elsewhere/…` would put a
 * stranger's story on the studio's (frameable) page.
 */
export function playsFrom(page: string, bundleUrl: URL): boolean {
  const here = new URL(page)
  return bundleUrl.protocol === here.protocol && bundleUrl.host === here.host
}

/** §4.6.4: every `assets[].url` resolves against the bundle's own directory. */
export function bundleBaseUrl(bundleUrl: URL | string): URL {
  return new URL('.', typeof bundleUrl === 'string' ? new URL(bundleUrl, 'https://x.invalid') : bundleUrl)
}

interface BundleLike {
  title?: unknown
  assets?: Record<string, { url?: unknown; kind?: unknown; pack?: { id?: unknown } } | undefined>
}

/**
 * Absolute URLs of every bundle asset, for the service worker's warm-up. A part packed on a page
 * is left out: the page carries its pixels, and the player never asks for the part's own PNG.
 */
export function assetUrls(bundle: unknown, baseUrl: URL | string): string[] {
  const assets = (bundle as BundleLike | null)?.assets
  if (!assets || typeof assets !== 'object') return []
  const out: string[] = []
  for (const entry of Object.values(assets)) {
    const url = entry?.url
    if (typeof url !== 'string' || url.length === 0) continue
    const page = entry?.pack?.id
    if (entry?.kind === 'texture' && typeof page === 'string' && assets[page]?.kind === 'pack') continue
    try {
      out.push(new URL(url, baseUrl).href)
    } catch {
      /* a malformed url is the loader's problem, not the cache's */
    }
  }
  return out
}

// ---------------------------------------------------------------------------
// Failures
// ---------------------------------------------------------------------------

// The bundle fetch's failure type lives with the fetch (`prefetch.ts`); re-exported here, where
// `describeFailure` reads it and where callers always found it.
export { HttpError }

export interface FriendlyError {
  /** One short line a child can have read out. */
  title: string
  /** One line for the grown-up — the actual reason. */
  detail: string
  /** Shown only behind "What went wrong?". */
  technical?: string
}

/** Turn whatever went wrong into something a tired parent can act on. */
export function describeFailure(err: unknown, bundleHref: string): FriendlyError {
  const technical = err instanceof Error ? `${err.name}: ${err.message}` : String(err)
  if (err instanceof HttpError) {
    if (err.status === 404) {
      return {
        title: 'This story has gone for a walk.',
        detail: 'The story file is not where the player expected it. Has it finished baking?',
        technical: `404 — ${bundleHref}`,
      }
    }
    return {
      title: 'The story would not open.',
      detail: `The server answered ${err.status}. Try again in a moment.`,
      technical: `${err.status} — ${bundleHref}`,
    }
  }
  if (err instanceof SyntaxError) {
    return {
      title: 'This story is a bit muddled.',
      detail: 'The story file could not be read. It may still be baking, or the download was cut short.',
      technical,
    }
  }
  return {
    title: 'We could not reach the story.',
    detail: 'Check the connection and try again.',
    technical,
  }
}

const FALLBACK_FONT =
  '"Lexend", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'

/**
 * The failure page. Plain DOM with inline styles, because this is the one
 * screen that must render with no bundle, no fonts and no service worker.
 */
export function renderFailure(host: HTMLElement, info: FriendlyError, onRetry?: () => void): void {
  host.textContent = ''
  const doc = host.ownerDocument ?? document
  const wrap = doc.createElement('div')
  wrap.dataset.hud = 'boot-error'
  wrap.setAttribute('role', 'alert')
  wrap.style.cssText = [
    'position:absolute',
    'inset:0',
    'display:grid',
    'place-items:center',
    'padding:24px',
    'text-align:center',
    `background:${THEME.shell}`,
    `color:${THEME.text}`,
    `font-family:${FALLBACK_FONT}`,
  ].join(';')

  const inner = doc.createElement('div')
  inner.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:24px;max-width:36rem'

  const face = doc.createElement('div')
  face.setAttribute('aria-hidden', 'true')
  face.textContent = '🌙'
  face.style.cssText = 'font-size:72px;line-height:1'

  const title = doc.createElement('h1')
  title.textContent = info.title
  title.style.cssText = 'margin:0;font-size:max(32px,5vw);font-weight:600;line-height:1.15'

  const detail = doc.createElement('p')
  detail.textContent = info.detail
  detail.style.cssText = 'margin:0;font-size:24px;line-height:1.4;color:' + THEME.textDim

  inner.append(face, title, detail)

  if (onRetry) {
    const retry = doc.createElement('button')
    retry.type = 'button'
    retry.dataset.hud = 'retry'
    retry.textContent = 'Try again'
    retry.style.cssText = [
      'min-height:88px',
      'min-width:88px',
      'padding:0 32px',
      'font:inherit',
      'font-size:24px',
      'font-weight:700',
      'border:0',
      'border-radius:999px',
      `background:${THEME.accent}`,
      `color:${THEME.onAccent}`,
      'cursor:pointer',
      'touch-action:manipulation',
    ].join(';')
    retry.addEventListener('click', onRetry)
    inner.appendChild(retry)
  }

  if (info.technical) {
    const details = doc.createElement('details')
    details.style.cssText = 'font-size:14px;color:#7a7067;max-width:100%'
    const summary = doc.createElement('summary')
    summary.textContent = 'What went wrong?'
    summary.style.cssText = 'cursor:pointer;min-height:44px;display:flex;align-items:center;justify-content:center'
    const pre = doc.createElement('pre')
    pre.textContent = info.technical
    pre.style.cssText = 'white-space:pre-wrap;word-break:break-word;text-align:left;margin:8px 0 0'
    details.append(summary, pre)
    inner.appendChild(details)
  }

  wrap.appendChild(inner)
  host.appendChild(wrap)
}

// ---------------------------------------------------------------------------
// Service worker
// ---------------------------------------------------------------------------

/**
 * Where the compiled service worker lives. It must sit **next to the page**,
 * not under `assets/`: a worker's default scope is its own directory, and one
 * emitted into `assets/` could never control `player.html`.
 *
 * `new URL('./sw.ts', import.meta.url)` is deliberately *not* used — without a
 * PWA plugin Vite copies that through as a raw `.ts` asset under `assets/`,
 * which is both the wrong scope and the wrong MIME type. `docs/WIRING.md` asks
 * the build owner for the one `rollupOptions` entry that emits `sw.js` at the
 * root; until it exists `register()` 404s, this returns null and the story
 * plays without an offline cache.
 */
export const SERVICE_WORKER_PATH = 'sw.js'

/**
 * Returns the registration, or null when the environment cannot have one
 * (insecure origin, no support, not built yet, blocked by a policy) — it never
 * throws, because a story must play without a cache.
 */
export async function registerPlayerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null
  if (typeof window !== 'undefined' && window.isSecureContext === false) return null
  // In `vite dev` nothing has compiled `sw.ts` yet, so a registration would only
  // put a 404 and a MIME error in the console of every page load.
  if (import.meta.env?.DEV) return null
  try {
    const url = new URL(SERVICE_WORKER_PATH, document.baseURI)
    return await navigator.serviceWorker.register(url.href, { type: 'module', scope: './' })
  } catch {
    return null
  }
}

/** Ask the worker to warm the content-addressed assets. Best effort. */
export function precacheAssets(urls: string[]): void {
  if (urls.length === 0) return
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  const post = (): void => {
    navigator.serviceWorker.controller?.postMessage({ type: 'sp-precache', urls })
  }
  if (navigator.serviceWorker.controller) post()
  else void navigator.serviceWorker.ready.then(post).catch(() => undefined)
}

// ---------------------------------------------------------------------------
// Mounting — shared by player.html and the React wrapper
// ---------------------------------------------------------------------------

export interface MountOptions {
  /** The url the bundle came from; its directory becomes `baseUrl` (§4.6.4). */
  bundleUrl: string
  /** `preview` keeps the gear visible and hides Exit. */
  ui?: 'kid' | 'preview'
  /** Starting grown-up prefs; the bundle's band defaults win where unset. */
  prefs?: Partial<HudPrefs>
  /** The grown-up panel's Exit. */
  onExit?: () => void
  /** Anything else `createPlayer` takes (audio port, device overrides, tests). */
  player?: Omit<CreatePlayerOptions, 'hud' | 'bundleUrl'>
  /** How a story still being made is polled (`stream.ts#watchBundle`); the defaults suit the app. */
  watch?: StreamWatchOptions
}

export interface MountedPlayer {
  player: PlayerHandle
  hud: HudHandle
  subtitles: SubtitleBand
  destroy(): void
}

/* ---------------------------------------------------- remembered prefs */

/** Where the grown-up's choices outlive the page (shared with the exported `player.html`). */
export const PREFS_STORAGE_KEY = 'story-player:prefs:v1'

/** What is remembered: only the playback speed (a per-viewer convenience). */
export interface SavedPrefs {
  rate?: number
}

function storageOf(storage?: Storage | null): Storage | null {
  if (storage !== undefined) return storage
  try {
    return (globalThis as { localStorage?: Storage }).localStorage ?? null
  } catch {
    return null // a sandboxed frame or blocked site data: the accessor itself throws
  }
}

/** The remembered prefs, or `{}` — never throws (private window, blocked storage, junk). */
export function loadSavedPrefs(storage?: Storage | null): SavedPrefs {
  try {
    const raw = storageOf(storage)?.getItem(PREFS_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as { rate?: unknown } | null
    const rate = parsed?.rate
    return typeof rate === 'number' && Number.isFinite(rate) && rate > 0 ? { rate: clampRate(rate) } : {}
  } catch {
    return {}
  }
}

/** Merge `patch` into the remembered prefs. Best effort: a story plays the same without it. */
export function savePrefs(patch: SavedPrefs, storage?: Storage | null): void {
  try {
    const s = storageOf(storage)
    if (!s) return
    const next = { ...loadSavedPrefs(s), ...patch }
    s.setItem(PREFS_STORAGE_KEY, JSON.stringify(next))
  } catch {
    /* quota, privacy mode: forget it */
  }
}

/* ----------------------------------------------- a story being made */

export interface StreamWatchOptions extends Pick<WatchOptions, 'intervalMs' | 'fastIntervalMs' | 'maxBackoffMs' | 'fetchImpl'> {
  /** Asset ids a revision added (the mount copies them into the audio port's table). */
  onAssets?: (ids: readonly string[]) => void
  /**
   * After the page ends a story whose job stopped (`endStream({failed: true})`), how long the poll
   * goes on (one look at once): a part published just before the job died may not be here yet.
   */
  finalLookMs?: number
}

/** `StreamWatchOptions.finalLookMs`'s default: a look at once, and a slower one after it. */
export const FINAL_LOOK_MS = 6_000

/**
 * Keep a player on a story still being made up to date: poll its `bundle.json` and
 * `appendBundle` every newer revision. A child waiting at the end of the ready parts gets a poll
 * at once and a faster pace after it; a deleted story (404) plays what it has and ends. Returns
 * null for a finished story (no open `stream` block) or a mount with no bundle url.
 */
export function watchStream(player: PlayerHandle, bundleUrl: string | undefined, opts: StreamWatchOptions = {}): BundleWatcher | null {
  if (!bundleUrl || !isOpen(streamOf(player.bundle))) return null
  const { onAssets, finalLookMs = FINAL_LOOK_MS, ...pace } = opts
  const watcher = watchBundle(bundleUrl, {
    ...pace,
    isWaiting: () => player.runner.isWaiting,
    onBundle: (b) => {
      const result = player.appendBundle(normalizeBundle(b) as Bundle)
      if (result.addedAssets.length > 0) onAssets?.(result.addedAssets)
    },
    onGone: () => player.control('end_stream'),
  })
  let waiting = player.runner.isWaiting
  player.on('state', (s) => {
    const now = s.phase === 'waiting'
    if (now && !waiting) watcher.poke()
    waiting = now
  })
  // "End here" or a deleted story: nothing more is wanted, stop now. The page's `endStream({failed})`
  // (its job stopped): the parts it published before that may still be on their way, so one look
  // at once and a last while (the poll also stops by itself on the `failed` revision).
  let lastLook: ReturnType<typeof setTimeout> | null = null
  player.on('stream', (s) => {
    if (!player.streamEnded || lastLook !== null) return
    if (s.state !== 'failed') {
      watcher.stop()
      return
    }
    watcher.poke()
    lastLook = setTimeout(() => watcher.stop(), finalLookMs)
  })
  return watcher
}

/** `Bundle.options.min_text_px` without pulling the whole Zod schema in. */
function minTextPx(bundle: unknown): number {
  const v = (bundle as { options?: { min_text_px?: unknown } } | null)?.options?.min_text_px
  return typeof v === 'number' && v > 0 ? v : 24
}

interface BundleAudioShape {
  assets?: Record<string, { url?: string }>
  cast?: Record<string, { voice?: unknown; speed?: unknown } | undefined>
}

/** `CastEntry.voice` per actor, so the Web Speech fallback can match voices. */
function voiceLookup(bundle: unknown): (actor: string | null) => string | null {
  const cast = (bundle as BundleAudioShape | null)?.cast ?? {}
  return (actor) => {
    if (!actor) return null
    const v = cast[actor]?.voice
    return typeof v === 'string' ? v : null
  }
}

/**
 * Create the HUD, hand it to `createPlayer`, and wire the one thing the player
 * does not wire itself: the grown-up panel's prefs.
 *
 * `createPlayer` already does `hud.onTap(...)` and `hud.onControl(...)`, so the
 * child's side needs nothing here. `onPrefs` is split because the runner owns
 * mode/questions/auto-answer/subtitles, volume belongs to the audio bus
 * (§8.4), and the playback speed to the player (clock and voice together).
 * The speed is remembered in `localStorage` (`PREFS_STORAGE_KEY`) and comes
 * back with the next story.
 */
export async function mountPlayer(host: HTMLElement, rawBundle: unknown, opts: MountOptions): Promise<MountedPlayer> {
  // Cast/props keyed by handle and asset `url`s, whichever way the baker wrote them (SKEL-STORY §3.5).
  const bundle: unknown = normalizeBundle(rawBundle)
  // The speed the grown-up chose last time, unless the caller says otherwise.
  const rememberedRate = opts.prefs?.rate ?? loadSavedPrefs().rate
  const hudPrefs: Partial<HudPrefs> = { ...(rememberedRate !== undefined ? { rate: rememberedRate } : {}), ...opts.prefs }
  const hud = createHud(host, {
    mode: opts.ui ?? 'kid',
    prefs: hudPrefs,
  })

  // `createPlayer` defaults to a *silent* port, so without this the story plays
  // with no narration at all. The bus decodes the baked Kokoro mp3s and falls
  // back to Web Speech — then to silence with estimated word timing — for any
  // line whose `audio` is missing (BUILD-OVERRIDES: "the player falls back to
  // Web Speech"). `createAudioPort` is audio.ts's own adapter onto the runner's
  // `AudioPort`; the mount only has to pass the bundle's asset table.
  const injected = opts.player?.audio ?? null
  const bus = injected ? null : new AudioBus()
  // A story still being made grows while it plays: the port's asset table gains the new parts'
  // sounds below, and the voices are looked up in the player's own (growing) bundle once it exists.
  const audioAssets = (bundle as BundleAudioShape | null)?.assets ?? null
  let voiceSource: unknown = bundle
  const audio = bus
    ? createAudioPort(bus, {
        assets: audioAssets,
        baseUrl: bundleBaseUrl(opts.bundleUrl),
        voiceFor: (actor) => voiceLookup(voiceSource)(actor),
      })
    : (injected ?? undefined)

  // The panel's two cross-port switches are starting values, not just HUD
  // state: a story that opens with reduced motion must not build a video plate.
  const startPrefs = {
    ...(rememberedRate !== undefined ? { rate: rememberedRate } : {}),
    ...opts.player?.prefs,
    ...(opts.prefs?.reducedMotion !== undefined ? { reducedMotion: opts.prefs.reducedMotion } : {}),
    ...(opts.prefs?.lowDistraction !== undefined ? { lowDistraction: opts.prefs.lowDistraction } : {}),
    ...(opts.prefs?.subtitles !== undefined ? { subtitles: opts.prefs.subtitles } : {}),
  }

  // A tap that lands while the story is still being set up is not lost. The start screen is up
  // from the first byte (and shows "Tap to start" once the textures are in, a moment before the
  // voices and the first scene are), and a child taps whatever is on screen: the audio is
  // unlocked inside that gesture, and the story starts the moment the player exists.
  let playerUp = false
  let tappedEarly = false
  hud.onTap(() => {
    if (playerUp) return
    tappedEarly = true
    void bus?.unlock()
  })

  let player: PlayerHandle
  try {
    player = await createPlayer(host, bundle as never, {
      ...opts.player,
      mode: opts.player?.mode ?? 'read_to_me',
      audio,
      bundleUrl: opts.bundleUrl,
      hud,
      prefs: startPrefs,
    })
  } catch (err) {
    hud.destroy()
    bus?.dispose()
    throw err
  }

  hud.setPrefs({
    mode: player.state.mode,
    questions: player.state.questions,
    subtitles: player.state.subtitles,
    reducedMotion: player.state.reducedMotion,
    lowDistraction: player.state.lowDistraction ?? false,
    rate: player.state.rate ?? 1,
    ...opts.prefs,
  })
  if (opts.prefs?.muted) bus?.setMuted(true)

  // The subtitle band is a DOM overlay like the HUD, so the mount owns it too:
  // `createPlayer` publishes `PlayerUiState.subtitle` but has no port for it.
  // On a phone it sits **below** the stage, never over the picture (§8.6).
  //
  // `SubtitleBand` styles itself for the placement it is told about but leaves
  // *where* to the host: `below` is `position: relative`, so it needs a slot of
  // its own. The slot is the letterbox under the stage — that is the whole
  // point of the portrait layout, and dropping the band straight into the host
  // would park it over the picture.
  const slot = host.ownerDocument.createElement('div')
  slot.className = 'sp-player-subs-slot'
  slot.style.cssText = 'position:absolute;left:0;right:0;display:flex;align-items:flex-start;pointer-events:none;z-index:20'
  host.appendChild(slot)

  const band = new SubtitleBand(slot, {
    minTextPx: minTextPx(bundle),
    placement: placementFor(window.innerWidth, window.innerHeight),
    enabled: player.state.subtitles,
  })

  // Sizes read once per resize, never per frame: `clientHeight` forces a
  // synchronous layout, and a state arrives every frame while a story plays
  // (and once per loaded texture while a part loads).
  let viewW = window.innerWidth
  let viewH = window.innerHeight
  let hostH = host.clientHeight
  let slotKey = ''
  const layoutSlot = (force = false): void => {
    const { scale, rect } = hud.getStage()
    const key = `${scale}|${rect ? `${rect.x},${rect.y},${rect.w},${rect.h}` : '-'}|${viewW}x${viewH}|${hostH}`
    if (!force && key === slotKey) return
    slotKey = key
    band.setStageScale(scale)
    const placement = placementFor(viewW, viewH)
    band.setPlacement(placement)
    const bottom = rect ? rect.y + rect.h : 0
    const gapBelowStage = rect ? Math.max(0, Math.round(hostH - bottom)) : 0
    const bandBelow = placement === 'below' && rect !== null && gapBelowStage > 0
    // `--sp-ctl-below` / `--sp-ctl-over` are the grown-up seek bar's room
    // (hud.ts writes them on the host): under the picture it keeps a strip
    // between the stage and the band; across the picture it lifts the band
    // above itself while it shows, the way a video player lifts captions.
    slot.style.top = bandBelow ? `calc(${Math.round(bottom)}px + var(--sp-ctl-below, 0px))` : ''
    // An `overlay` band belongs to the *picture*, so its bottom is the stage's
    // bottom and not the window's. Pinned to the window it hangs into the
    // letterbox, where `.sp-hud-mat` — one element whose 9999px box-shadow
    // paints everything outside the stage rect — is drawn on top of it and
    // takes the lower half of the second line with it. With subtitles on, a
    // grown-up reading along loses half the words.
    slot.style.bottom = bandBelow ? '0' : `calc(${gapBelowStage}px + var(--sp-ctl-over, 0px))`
    slot.dataset.placement = bandBelow ? 'below' : 'overlay'
    if (!bandBelow) band.setPlacement('overlay')
  }

  layoutSlot(true)
  player.on('state', (s) => {
    // A no-op unless the stage moved (the HUD's own ResizeObserver re-measures it).
    layoutSlot()
    applyPlayerUiState(band, s)
  })
  const onResize = (): void => {
    viewW = window.innerWidth
    viewH = window.innerHeight
    hostH = host.clientHeight
    layoutSlot(true)
  }
  window.addEventListener('resize', onResize)
  window.addEventListener('orientationchange', onResize)
  // An inline preview can change size without the window doing so.
  const RO = (window as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver
  const hostObserver = RO ? new RO(() => onResize()) : null
  hostObserver?.observe(host)

  // §8.4: "One tap unlocks Web Audio, the video element and the screen wake
  // lock." `handle.tap()` only feeds the runner's tap policy — it is
  // `handle.start()` that awaits `audio.unlock()`, and that must happen inside
  // the gesture's own task. So the mount turns the *first* tap, wherever it
  // lands, into the unlock. `hud.onTap` appends, so the player still sees it.
  let needsGesture = player.state.needsGesture
  player.on('state', (s) => {
    needsGesture = s.needsGesture
  })
  hud.onTap(() => {
    if (!needsGesture) return
    needsGesture = false
    void player.start()
  })
  playerUp = true
  if (tappedEarly && needsGesture) {
    needsGesture = false
    void player.start()
  }

  hud.onPrefs((patch) => {
    const { runner, audio: audioPrefs, rate, reducedMotion, lowDistraction } = splitPrefs(patch)
    if (Object.keys(runner).length > 0) player.setPrefs(runner)
    if (audioPrefs.volume !== undefined) bus?.setVolume(audioPrefs.volume)
    // The M hotkey. The bus keeps the volume, so un-muting restores the level.
    if (audioPrefs.muted !== undefined) bus?.setMuted(audioPrefs.muted)
    // Speed and reduced motion go through the player, not straight to the
    // bus: `setPrefs` owns both (the clock + `AudioPort.setRate` for the speed,
    // `RendererPort.setReducedMotion` + `PlayerUiState.reducedMotion` for the
    // motion), so the HUD, the renderer and the audio path never disagree.
    const crossPort: { rate?: number; reducedMotion?: boolean; lowDistraction?: boolean } = {}
    if (rate !== undefined) crossPort.rate = rate
    if (reducedMotion !== undefined) crossPort.reducedMotion = reducedMotion
    if (lowDistraction !== undefined) crossPort.lowDistraction = lowDistraction
    if (Object.keys(crossPort).length > 0) player.setPrefs(crossPort)
    // Remembered for the next story (and the next visit).
    if (rate !== undefined) savePrefs({ rate: clampRate(rate) })
  })

  // `exit` is a `PlayerControl`, so the player sees it too; the mount needs its
  // own copy to leave the page or the route. `hud.onControl` appends, so this
  // does not displace the handler `createPlayer` just registered.
  const onExit = opts.onExit
  if (onExit) {
    hud.onControl((c) => {
      if (c === 'exit') onExit()
    })
  }

  // A story still being made (docs/STORY-STREAMING.md): the page opened it on its first ready
  // part, and `bundle.json` grows by a part at a time. Watch it and fold each revision in; the
  // watcher stops by itself once the story is complete, failed or deleted.
  voiceSource = player.bundle
  const watcher = watchStream(player, opts.bundleUrl, {
    ...(opts.watch ?? {}),
    onAssets: (ids) => {
      if (!audioAssets) return
      for (const id of ids) {
        const a = player.bundle.assets[id]
        if (a) audioAssets[id] = a
      }
    },
  })
  // "End here" stops the watcher through the player's `stream` event (`watchStream`).

  return {
    player,
    hud,
    subtitles: band,
    destroy() {
      watcher?.stop()
      window.removeEventListener('resize', onResize)
      window.removeEventListener('orientationchange', onResize)
      hostObserver?.disconnect()
      player.destroy()
      hud.destroy()
      band.dispose()
      slot.remove()
      bus?.dispose()
    },
  }
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

/**
 * `bundle.json`, taking the copy `prefetch.ts#prefetchBundle` fetched a moment ago (the Stories
 * list starts one when a pointer rests on "Play") instead of asking again. A prefetched copy is
 * used once and is never older than `PREFETCH_FRESH_MS`: a re-baked story is fetched again. A copy
 * of an incomplete or failed story is asked for again too: a retry may have completed that
 * same story since. The browser revalidates by ETag, so an unchanged one is a 304.
 */
export async function fetchBundle(url: URL | string, signal?: AbortSignal): Promise<unknown> {
  const href = typeof url === 'string' ? url : url.href
  const early = takePrefetched(href)
  if (early) {
    try {
      const body = await early
      if (streamOf(body as Bundle).state === 'complete') return body
    } catch {
      /* the prefetch failed (offline for a moment?): ask again, for real */
    }
  }
  return fetchBundleJson(href, signal)
}

function ensureHost(doc: Document): HTMLElement {
  const existing = doc.getElementById('player')
  if (existing) {
    existing.textContent = ''
    return existing
  }
  const host = doc.createElement('div')
  host.id = 'player'
  host.style.cssText = 'position:fixed;inset:0'
  doc.body.appendChild(host)
  return host
}

export interface StandaloneOptions {
  /** Where the player mounts. Default: `#player`, created if missing. */
  host?: HTMLElement
  /** Override the bundle URL (tests, embedding). */
  src?: string
  ui?: 'kid' | 'preview'
  /** Skip service-worker registration (tests, `http://` on a LAN IP). */
  serviceWorker?: boolean
}

export interface StandaloneHandle {
  mounted: MountedPlayer | null
  bundleUrl: URL
  destroy(): void
}

/** Mount the standalone player. Resolves once the player exists (or failed). */
export async function bootstrap(opts: StandaloneOptions = {}): Promise<StandaloneHandle> {
  if (typeof document === 'undefined') throw new Error('the standalone player needs a document')
  const doc = document
  const host = opts.host ?? ensureHost(doc)
  const bundleUrl = resolveBundleUrl(window.location.href, opts.src)

  if (opts.serviceWorker !== false) void registerPlayerServiceWorker()

  let mounted: MountedPlayer | null = null
  try {
    if (!playsFrom(window.location.href, bundleUrl)) throw new Error('This story is not from this player\'s site.')
    const bundle = normalizeBundle(await fetchBundle(bundleUrl))
    const title = (bundle as BundleLike | null)?.title
    if (typeof title === 'string' && title.length > 0) doc.title = title
    precacheAssets(assetUrls(bundle, bundleBaseUrl(bundleUrl)))
    mounted = await mountPlayer(host, bundle, {
      bundleUrl: bundleUrl.href,
      ...(opts.ui ? { ui: opts.ui } : {}),
      onExit: () => {
        if (window.history.length > 1) window.history.back()
        else window.close()
      },
    })
  } catch (err) {
    renderFailure(host, describeFailure(err, bundleUrl.href), () => window.location.reload())
  }

  return {
    mounted,
    bundleUrl,
    destroy() {
      mounted?.destroy()
      host.textContent = ''
    },
  }
}

/** True when this module is the page's entry (not an import inside a test). */
function isPageEntry(): boolean {
  return typeof document !== 'undefined' && document.documentElement.dataset.player === 'standalone'
}

if (isPageEntry()) {
  void bootstrap().catch((err: unknown) => {
    const host = document.getElementById('player')
    if (host) renderFailure(host, describeFailure(err, window.location.href), () => window.location.reload())
  })
}
